import { useEffect } from 'react';
import { useToast } from './use-toast';

interface KeyboardShortcuts {
  onSearch?: () => void;
  onToggleFilters?: () => void;
  onClearFilters?: () => void;
  onNextTab?: () => void;
  onPrevTab?: () => void;
}

export const useKeyboardShortcuts = ({
  onSearch,
  onToggleFilters,
  onClearFilters,
  onNextTab,
  onPrevTab
}: KeyboardShortcuts) => {
  const { toast } = useToast();

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Only trigger if not typing in an input
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // Cmd/Ctrl + K - Focus search
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        onSearch?.();
        toast({
          title: "Search Focused",
          description: "Start typing to search for movies",
        });
      }

      // Cmd/Ctrl + F - Toggle filters
      if ((event.metaKey || event.ctrlKey) && event.key === 'f') {
        event.preventDefault();
        onToggleFilters?.();
      }

      // Cmd/Ctrl + Backspace - Clear filters
      if ((event.metaKey || event.ctrlKey) && event.key === 'Backspace') {
        event.preventDefault();
        onClearFilters?.();
        toast({
          title: "Filters Cleared",
          description: "All filters have been reset",
        });
      }

      // Arrow Right - Next tab
      if (event.altKey && event.key === 'ArrowRight') {
        event.preventDefault();
        onNextTab?.();
      }

      // Arrow Left - Previous tab
      if (event.altKey && event.key === 'ArrowLeft') {
        event.preventDefault();
        onPrevTab?.();
      }

      // ? - Show shortcuts help
      if (event.key === '?' && event.shiftKey) {
        event.preventDefault();
        toast({
          title: "Keyboard Shortcuts",
          description: (
            <div className="text-xs space-y-1 mt-2">
              <p><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">⌘K</kbd> Focus Search</p>
              <p><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">⌘F</kbd> Toggle Filters</p>
              <p><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">⌘⌫</kbd> Clear Filters</p>
              <p><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">Alt+→</kbd> Next Tab</p>
              <p><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">Alt+←</kbd> Prev Tab</p>
            </div>
          ),
        });
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onSearch, onToggleFilters, onClearFilters, onNextTab, onPrevTab, toast]);
};